import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import axios from "axios";
import { useTheme } from "../../theme";
import GoogleLogo from "../shared/GoogleLogo";

const PLATFORM_DEFS = [
  { id: "Steam", label: "Steam" },
  { id: "Playstation", label: "PlayStation" },
  { id: "Xbox", label: "Xbox" },
  { id: "Discord", label: "Discord" },
];

const PLATFORM_VALIDATION = {
  Steam: {
    regex: /^[a-zA-Z0-9_]{2,32}$/,
    hint: "2–32 characters: letters, numbers, underscores only",
  },
  Playstation: {
    regex: /^[a-zA-Z][a-zA-Z0-9_-]{2,15}$/,
    hint: "3–16 characters, must start with a letter (letters, numbers, _ -)",
  },
  Xbox: {
    regex: /^[a-zA-Z][a-zA-Z0-9 ]{2,11}$/,
    hint: "3–12 characters, must start with a letter (letters, numbers, spaces)",
  },
  Discord: {
    regex: /^[a-z0-9_.]{2,32}$/,
    hint: "2–32 lowercase characters (letters, numbers, _ .)",
  },
};

const STEPS = [
  { n: "01", title: "Your details", desc: "choose a username and password" },
  { n: "02", title: "Your platforms", desc: "link your gaming accounts" },
  { n: "03", title: "About you", desc: "tell others about yourself" },
];

const SignUp = (props) => {
  const { p, fonts } = useTheme();
  const [step, setStep] = useState(0);
  const [values, setValues] = useState({
    username: "", password: "", confirmPassword: "",
    Steam: "", Playstation: "", Xbox: "", Discord: "", aboutme: "",
  });
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState({});
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleMsg, setGoogleMsg] = useState('');
  const googleTimer = useRef(null);
  const navigate = useNavigate();

  useEffect(() => () => clearTimeout(googleTimer.current), []);

  function handleGoogle() {
    if (googleLoading) return;
    setGoogleLoading(true);
    setGoogleMsg('');
    setTimeout(() => {
      setGoogleLoading(false);
      setGoogleMsg('Google authentication is currently unavailable. Please create an account below.');
      googleTimer.current = setTimeout(() => setGoogleMsg(''), 4000);
    }, 1500);
  }
  const isWide = typeof window !== 'undefined' && window.matchMedia("(min-width:768px)").matches;

  const handleChange = (prop) => (e) =>
    setValues((prev) => ({ ...prev, [prop]: e.target.value }));

  const togglePlatform = (id) =>
    setSelectedPlatforms((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const reqLength = values.password.length >= 8;
  const reqMatch = values.password === values.confirmPassword && values.password !== "";

  const validateStep = () => {
    const e = {};
    if (step === 0) {
      if (values.username.length < 8) e.username = "Username must be at least 8 characters";
      if (!reqLength) e.password = "Password must be at least 8 characters";
      if (!reqMatch) e.confirmPassword = "Passwords do not match";
    }
    if (step === 1) {
      if (selectedPlatforms.length === 0) {
        e.platforms = "Select at least one platform";
      } else {
        let anyFilled = false;
        for (const pid of selectedPlatforms) {
          const val = values[pid].trim();
          if (val !== "") {
            anyFilled = true;
            const rule = PLATFORM_VALIDATION[pid];
            if (rule && !rule.regex.test(val)) e[pid] = rule.hint;
          }
        }
        if (!anyFilled) e.platforms = "Enter a username for at least one selected platform";
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => { if (validateStep()) setStep((s) => s + 1); };
  const handleBack = () => { setErrors({}); setStep((s) => s - 1); };

  const handleSubmit = () => {
    axios
      .post(`${process.env.REACT_APP_API_URL}/register/`, {
        username: values.username, password: values.password,
        link_steam: values.Steam, link_xbox: values.Xbox,
        link_psn: values.Playstation, link_discord: values.Discord,
      })
      .then(() =>
        axios.post(`${process.env.REACT_APP_API_URL}/api/token/login`, {
          username: values.username, password: values.password,
        }).then((response) => {
          props.handleToken(response.data["auth_token"]);
          navigate("/");
        })
      )
      .catch((error) => {
        if (error.response?.data?.username === "A user with that username already exists.") {
          setErrors({ username: "A user with that username already exists." });
          setStep(0);
        } else {
          setErrors({ submit: "Something went wrong. Please try again." });
        }
      });
  };

  const inputStyle = {
    width: '100%', background: 'transparent', border: 'none', outline: 'none',
    fontFamily: fonts.mono, fontSize: 14, color: p.textHi, caretColor: p.accent, flex: 1,
  };

  const fieldBox = (hasError) => ({
    border: `1px solid ${hasError ? p.rose : p.borderHi}`,
    padding: '13px 14px', fontFamily: fonts.mono, fontSize: 14,
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4,
  });

  const errorText = { fontFamily: fonts.mono, fontSize: 11, color: p.rose, marginBottom: 14 };

  const renderStep = () => {
    if (step === 0) {
      return (
        <>
          <div
            onClick={handleGoogle}
            style={{
              padding: '13px 0', textAlign: 'center',
              border: `1px solid ${p.borderHi}`, background: 'transparent',
              fontFamily: fonts.mono, fontSize: 13, letterSpacing: '.06em',
              color: p.text, cursor: 'pointer',
              display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10,
              opacity: googleLoading ? 0.6 : 1,
              transition: 'opacity .15s',
            }}
          >
            {googleLoading ? (
              <span style={{ color: p.textDim }}>CONNECTING...</span>
            ) : (
              <><GoogleLogo size={18} /> SIGN UP WITH GOOGLE</>
            )}
          </div>

          {googleMsg && (
            <div style={{
              padding: '10px 14px', marginTop: 12,
              border: `1px solid ${p.warn}`, color: p.warn,
              fontFamily: fonts.mono, fontSize: 12,
            }}>
              {googleMsg}
            </div>
          )}

          <div style={{
            display: 'flex', alignItems: 'center', gap: 14,
            margin: '20px 0 24px',
          }}>
            <span style={{ flex: 1, borderTop: `1px dashed ${p.border}` }} />
            <span style={{ fontFamily: fonts.mono, fontSize: 11, color: p.textDim, letterSpacing: '.08em' }}>or</span>
            <span style={{ flex: 1, borderTop: `1px dashed ${p.border}` }} />
          </div>

          {/* username */}
          <label style={{ display: 'block', fontFamily: fonts.mono, fontSize: 10, letterSpacing: '.14em', color: p.textDim, textTransform: 'uppercase', marginBottom: 8 }}>USERNAME</label>
          <div style={fieldBox(!!errors.username)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
              <span style={{ color: p.accent }}>›</span>
              <input value={values.username} onChange={handleChange("username")} maxLength={20} placeholder="your username" style={inputStyle} />
            </div>
          </div>
          {errors.username && <div style={errorText}>{errors.username}</div>}
          {!errors.username && <div style={{ height: 14 }} />}

          {/* password */}
          <label style={{ display: 'block', fontFamily: fonts.mono, fontSize: 10, letterSpacing: '.14em', color: p.textDim, textTransform: 'uppercase', marginBottom: 8 }}>PASSWORD</label>
          <div style={fieldBox(!!errors.password)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
              <span style={{ color: p.accent }}>›</span>
              <input type={showPassword ? 'text' : 'password'} value={values.password} onChange={handleChange("password")} placeholder="••••••••" style={inputStyle} />
            </div>
            <span onClick={() => setShowPassword(v => !v)} style={{ color: p.textDim, cursor: 'pointer', fontSize: 12, userSelect: 'none' }}>{showPassword ? 'hide' : 'show'}</span>
          </div>
          {errors.password && <div style={errorText}>{errors.password}</div>}
          {!errors.password && <div style={{ height: 14 }} />}

          {/* confirm password */}
          <label style={{ display: 'block', fontFamily: fonts.mono, fontSize: 10, letterSpacing: '.14em', color: p.textDim, textTransform: 'uppercase', marginBottom: 8 }}>CONFIRM PASSWORD</label>
          <div style={fieldBox(!!errors.confirmPassword)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
              <span style={{ color: p.accent }}>›</span>
              <input type={showConfirm ? 'text' : 'password'} value={values.confirmPassword} onChange={handleChange("confirmPassword")} placeholder="••••••••" style={inputStyle} />
            </div>
            <span onClick={() => setShowConfirm(v => !v)} style={{ color: p.textDim, cursor: 'pointer', fontSize: 12, userSelect: 'none' }}>{showConfirm ? 'hide' : 'show'}</span>
          </div>
          {errors.confirmPassword && <div style={errorText}>{errors.confirmPassword}</div>}

          {/* requirement indicators */}
          {(values.password.length > 0 || values.confirmPassword.length > 0) && (
            <div style={{ marginTop: 12, marginBottom: 16, padding: '12px 14px', border: `1px dashed ${p.border}`, fontFamily: fonts.mono, fontSize: 12 }}>
              <div style={{ color: reqLength ? p.accent : p.textDim, marginBottom: 4 }}>
                {reqLength ? '✓' : '○'} at least 8 characters
              </div>
              <div style={{ color: reqMatch ? p.accent : p.textDim }}>
                {reqMatch ? '✓' : '○'} passwords match
              </div>
            </div>
          )}

          <div onClick={handleNext} style={{
            marginTop: 20, padding: '14px 0', textAlign: 'center',
            background: p.accent, color: p.bg,
            fontFamily: fonts.mono, fontSize: 13, fontWeight: 500, letterSpacing: '.08em', cursor: 'pointer',
          }}>CONTINUE →</div>
        </>
      );
    }

    if (step === 1) {
      return (
        <>
          {/* platform cards */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
            {PLATFORM_DEFS.map(plat => {
              const sel = selectedPlatforms.includes(plat.id);
              return (
                <div key={plat.id} onClick={() => togglePlatform(plat.id)} style={{
                  border: `1px solid ${sel ? p.accent : p.borderHi}`,
                  background: sel ? p.accentBg : 'transparent',
                  padding: '16px', cursor: 'pointer', textAlign: 'center',
                  transition: 'border-color .15s',
                }}>
                  <div style={{ fontFamily: fonts.mono, fontSize: 13, color: sel ? p.accent : p.text, letterSpacing: '.06em' }}>
                    {sel && '✓ '}{plat.label.toUpperCase()}
                  </div>
                </div>
              );
            })}
          </div>

          {/* platform username fields */}
          {selectedPlatforms.map(pid => {
            const def = PLATFORM_DEFS.find(x => x.id === pid);
            return (
              <div key={pid} style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontFamily: fonts.mono, fontSize: 10, letterSpacing: '.14em', color: p.textDim, textTransform: 'uppercase', marginBottom: 8 }}>
                  {def.label} USERNAME
                </label>
                <div style={fieldBox(!!errors[pid])}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                    <span style={{ color: p.accent }}>›</span>
                    <input value={values[pid]} onChange={handleChange(pid)} placeholder={`your ${def.label.toLowerCase()} username`} style={inputStyle} />
                  </div>
                </div>
                {errors[pid] && <div style={errorText}>{errors[pid]}</div>}
              </div>
            );
          })}

          {errors.platforms && <div style={errorText}>{errors.platforms}</div>}

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <span onClick={handleBack} style={{
              padding: '14px 28px', border: `1px solid ${p.borderHi}`, color: p.text,
              fontFamily: fonts.mono, fontSize: 13, letterSpacing: '.08em', cursor: 'pointer',
            }}>← BACK</span>
            <span onClick={handleNext} style={{
              flex: 1, padding: '14px 0', textAlign: 'center',
              background: p.accent, color: p.bg,
              fontFamily: fonts.mono, fontSize: 13, fontWeight: 500, letterSpacing: '.08em', cursor: 'pointer',
            }}>CONTINUE →</span>
          </div>
        </>
      );
    }

    // step 2: about you
    return (
      <>
        <label style={{ display: 'block', fontFamily: fonts.mono, fontSize: 10, letterSpacing: '.14em', color: p.textDim, textTransform: 'uppercase', marginBottom: 8 }}>
          ABOUT ME (OPTIONAL)
        </label>
        <textarea
          value={values.aboutme}
          onChange={handleChange("aboutme")}
          maxLength={256}
          rows={5}
          placeholder="a short bio so others know who you are..."
          style={{
            width: '100%', background: 'transparent',
            border: `1px solid ${p.borderHi}`, padding: '14px',
            fontFamily: fonts.sans, fontSize: 14, color: p.textHi,
            caretColor: p.accent, outline: 'none', resize: 'vertical',
          }}
        />
        <div style={{ fontFamily: fonts.mono, fontSize: 11, color: p.textDim, marginTop: 6, textAlign: 'right' }}>
          {values.aboutme.length} / 256
        </div>

        {errors.submit && <div style={{ ...errorText, marginTop: 14 }}>{errors.submit}</div>}

        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <span onClick={handleBack} style={{
            padding: '14px 28px', border: `1px solid ${p.borderHi}`, color: p.text,
            fontFamily: fonts.mono, fontSize: 13, letterSpacing: '.08em', cursor: 'pointer',
          }}>← BACK</span>
          <span onClick={handleSubmit} style={{
            flex: 1, padding: '14px 0', textAlign: 'center',
            background: p.accent, color: p.bg,
            fontFamily: fonts.mono, fontSize: 13, fontWeight: 500, letterSpacing: '.08em', cursor: 'pointer',
          }}>CREATE ACCOUNT ↵</span>
        </div>
      </>
    );
  };

  return (
    <div style={{ minHeight: '100%', background: p.bg, color: p.text, fontFamily: fonts.sans }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: isWide ? '320px 1fr' : '1fr',
        minHeight: 780,
        borderBottom: `1px solid ${p.border}`,
      }}>
        {/* sidebar — desktop only */}
        {isWide && (
          <aside style={{
            borderRight: `1px solid ${p.border}`, padding: '40px 32px',
            background: p.bgPanel, display: 'flex', flexDirection: 'column',
          }}>
            <div style={{ fontFamily: fonts.mono, fontSize: 10, letterSpacing: '.18em', color: p.textDim, textTransform: 'uppercase', marginBottom: 24 }}>
              § /create_account
            </div>
            <div style={{ fontFamily: fonts.serif, fontSize: 24, fontWeight: 500, letterSpacing: '-.015em', color: p.textHi, marginBottom: 32 }}>
              Create account
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {STEPS.map((s, i) => (
                <div key={s.n} style={{
                  display: 'grid', gridTemplateColumns: '32px 1fr', gap: 14,
                  padding: '14px 0',
                  borderBottom: i < STEPS.length - 1 ? `1px dashed ${p.border}` : 'none',
                }}>
                  <span style={{
                    width: 26, height: 26,
                    border: `1px solid ${i <= step ? p.accent : p.borderHi}`,
                    background: i < step ? p.accent : 'transparent',
                    color: i < step ? p.bg : (i === step ? p.accent : p.textFaint),
                    fontFamily: fonts.mono, fontSize: 11,
                    display: 'grid', placeItems: 'center',
                  }}>{i < step ? '✓' : s.n}</span>
                  <div>
                    <div style={{ fontSize: 14, color: i <= step ? p.textHi : p.text, fontWeight: 500 }}>{s.title}</div>
                    <div style={{ fontSize: 12, color: p.textDim, marginTop: 2 }}>{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{
              marginTop: 'auto', paddingTop: 48, fontFamily: fonts.mono, fontSize: 11, letterSpacing: '.06em',
              display: 'flex', justifyContent: 'space-between',
            }}>
              <NavLink to="/" style={{ color: p.textDim, textDecoration: 'none' }}>← back to home</NavLink>
              <NavLink to="/login" style={{ color: p.accent, textDecoration: 'none' }}>sign in</NavLink>
            </div>
          </aside>
        )}

        {/* main content */}
        <main style={{ padding: isWide ? '80px 64px' : '32px 24px', display: 'grid', placeItems: 'start center' }}>
          <div style={{ width: '100%', maxWidth: 480 }}>
            {/* mobile step label */}
            {!isWide && (
              <div style={{ fontFamily: fonts.mono, fontSize: 11, color: p.textDim, marginBottom: 16, letterSpacing: '.06em' }}>
                step {step + 1} of {STEPS.length} / {STEPS[step].title}
              </div>
            )}

            <div style={{ fontFamily: fonts.mono, fontSize: 10, letterSpacing: '.18em', color: p.accent, textTransform: 'uppercase', marginBottom: 10 }}>
              STEP {STEPS[step].n}
            </div>
            <h2 style={{
              fontFamily: fonts.serif, fontSize: 42, fontWeight: 400,
              letterSpacing: '-.02em', margin: '0 0 8px', color: p.textHi, lineHeight: 1.05,
            }}>
              {STEPS[step].title}
            </h2>
            <p style={{ fontSize: 14.5, color: p.textDim, marginBottom: 36, lineHeight: 1.5 }}>
              {STEPS[step].desc}.
            </p>

            {renderStep()}

            {/* progress dots */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 22 }}>
              {STEPS.map((_, i) => (
                <span key={i} style={{
                  width: i === step ? 20 : 6, height: 3,
                  background: i <= step ? p.accent : p.border,
                  transition: 'width .2s, background .2s',
                }} />
              ))}
            </div>

            {/* mobile bottom links */}
            {!isWide && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24, fontFamily: fonts.mono, fontSize: 11, letterSpacing: '.06em' }}>
                <NavLink to="/" style={{ color: p.textDim, textDecoration: 'none' }}>← back to home</NavLink>
                <NavLink to="/login" style={{ color: p.accent, textDecoration: 'none' }}>sign in</NavLink>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default SignUp;
